import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Clapperboard, Plus, Trash2, ArrowLeft, Camera, Sparkles, Check, Circle,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import ShotListImportDialog from "@/components/ShotListImportDialog";

const STATUS_META = {
  planned: { label: "Planned", color: "#F5B82E", Icon: Circle },
  shot:    { label: "Shot",    color: "#22C55E", Icon: Check },
  skipped: { label: "Skipped", color: "#71717A", Icon: Circle },
};

export default function ProjectShotList() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [rows, setRows]       = useState([]);
  const [counts, setCounts]   = useState({planned:0, shot:0, skipped:0});
  const [movements, setMovements] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [dayFilter, setDayFilter] = useState("all");
  const [form, setForm]       = useState(blankForm());

  function blankForm() {
    return {
      scene_number: "", shot_number: "", description: "",
      lens: "", camera_movement: "static",
      cast_in_shot_raw: "", vfx: false,
      shoot_day: "", status: "planned",
    };
  }

  async function load() {
    try {
      const [{ data: p }, { data: s }, { data: m }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/shots`),
        api.get(`/projects/preprod/meta`),
      ]);
      setProject(p.project || p);
      setRows(s.shots || []);
      setCounts(s.counts || {});
      setMovements(m.camera_movements || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load shots");
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  async function add(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const cast_in_shot = form.cast_in_shot_raw.split(",").map(s => s.trim()).filter(Boolean);
      await api.post(`/projects/${id}/shots`, {
        scene_number: form.scene_number,
        shot_number:  form.shot_number,
        description:  form.description,
        lens:         form.lens,
        camera_movement: form.camera_movement,
        cast_in_shot,
        vfx:          form.vfx,
        shoot_day:    form.shoot_day,
        status:       form.status,
      });
      toast.success("Shot added");
      setOpenAdd(false);
      setForm(blankForm());
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed");
    } finally { setSaving(false); }
  }

  async function toggleStatus(shot) {
    const order = ["planned", "shot", "skipped"];
    const next = order[(order.indexOf(shot.status || "planned") + 1) % order.length];
    try {
      await api.patch(`/projects/${id}/shots/${shot.id}`, { status: next });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  }

  async function remove(shotId) {
    if (!window.confirm("Delete this shot?")) return;
    try {
      await api.delete(`/projects/${id}/shots/${shotId}`);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  }

  const days = useMemo(() => {
    const set = new Set(rows.map(r => r.shoot_day || "").filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (dayFilter === "all") return rows;
    if (dayFilter === "_unassigned") return rows.filter(r => !r.shoot_day);
    return rows.filter(r => r.shoot_day === dayFilter);
  }, [rows, dayFilter]);

  const scenes = useMemo(() => {
    const groups = {};
    for (const r of filtered) {
      const key = r.scene_number || "—";
      (groups[key] = groups[key] || []).push(r);
    }
    return Object.entries(groups);
  }, [filtered]);

  const shotProgress = counts.shot || 0;
  const totalShots   = rows.length || 0;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="project-shotlist-page">
      <button
        onClick={() => nav(`/p/${id}`)}
        className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
        data-testid="back-to-project">
        <ArrowLeft size={12} /> Back to Project
      </button>

      <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Pre-Production · Shot List</div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight" data-testid="shotlist-title">
            {project?.title || "Shot List"}
          </h1>
          <div className="mt-2 text-sm text-white/55" data-testid="shotlist-progress">
            {shotProgress} of {totalShots} shots complete · {counts.planned || 0} planned · {counts.skipped || 0} skipped
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {days.length > 0 && (
            <Select value={dayFilter} onValueChange={setDayFilter}>
              <SelectTrigger className="bg-black/40 border-white/15 h-9 text-xs w-[160px]" data-testid="shotlist-day-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All shoot days</SelectItem>
                {days.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                <SelectItem value="_unassigned">— Unassigned</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => setOpenImport(true)}
            variant="outline"
            className="rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4 border-white/20 hover:border-fuchsia-400/60 hover:text-fuchsia-300"
            data-testid="shotlist-import-btn">
            <Upload size={14} className="mr-1.5" /> Import
          </Button>
          <Button onClick={() => setOpenAdd(true)}
            className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4"
            data-testid="shotlist-add-btn">
            <Plus size={14} className="mr-1.5" /> Add Shot
          </Button>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="mt-8 cs-card rounded-2xl p-10 text-center" data-testid="shotlist-empty">
          <Clapperboard size={28} className="mx-auto text-white/40" />
          <div className="mt-3 text-sm text-white/60">No shots planned yet.</div>
          <div className="mt-1 text-xs text-white/45">Start by adding your master shot for Scene 1, then work through coverage.</div>
        </div>
      )}

      {scenes.length > 0 && (
        <div className="mt-6 space-y-5" data-testid="shotlist-grouped">
          {scenes.map(([scene, list]) => (
            <div key={scene} className="cs-card rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center font-display text-lg tabular-nums">
                    {scene}
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Scene</div>
                    <div className="text-sm font-medium">
                      {list.length} shot{list.length===1?"":"s"} ·
                      {" "}{list.filter(s => s.status === "shot").length} done
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {list.map(s => (
                  <ShotRow key={s.id} shot={s} onToggle={toggleStatus} onRemove={remove} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ShotListImportDialog
        open={openImport}
        onOpenChange={setOpenImport}
        projectId={id}
        onImported={load}
      />

      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-2xl" data-testid="shotlist-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Add a shot</DialogTitle>
            <DialogDescription className="text-white/55">Scene + Shot number combo must be unique per shooting.</DialogDescription>
          </DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Scene # *">
                <Input required value={form.scene_number} data-testid="shot-scene"
                  onChange={e => setForm({...form, scene_number: e.target.value})}
                  placeholder="e.g. 5 or 12A"
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Shot # *">
                <Input required value={form.shot_number} data-testid="shot-number"
                  onChange={e => setForm({...form, shot_number: e.target.value})}
                  placeholder="e.g. 5A"
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Shoot Day">
                <Input value={form.shoot_day} data-testid="shot-day"
                  onChange={e => setForm({...form, shoot_day: e.target.value})}
                  placeholder="Day 3 or 2026-08-12"
                  className="bg-black/40 border-white/15" />
              </Field>
              <div className="sm:col-span-3">
                <Field label="Description">
                  <Textarea value={form.description} data-testid="shot-desc"
                    onChange={e => setForm({...form, description: e.target.value})}
                    placeholder="e.g. CU on the hero as he lights the cigarette; rack focus to reveal antagonist"
                    className="bg-black/40 border-white/15 min-h-[70px]" />
                </Field>
              </div>
              <Field label="Lens">
                <Input value={form.lens} data-testid="shot-lens"
                  onChange={e => setForm({...form, lens: e.target.value})}
                  placeholder="e.g. 85mm · 24-70 zoom"
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Camera Movement">
                <Select value={form.camera_movement} onValueChange={(v)=>setForm({...form, camera_movement: v})}>
                  <SelectTrigger className="bg-black/40 border-white/15" data-testid="shot-movement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {movements.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                  <SelectTrigger className="bg-black/40 border-white/15" data-testid="shot-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-3">
                <Field label="Cast in Shot (comma separated)">
                  <Input value={form.cast_in_shot_raw} data-testid="shot-cast"
                    onChange={e => setForm({...form, cast_in_shot_raw: e.target.value})}
                    placeholder="Hero, Villain, Extra x3"
                    className="bg-black/40 border-white/15" />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-xs text-white/70 sm:col-span-3" data-testid="shot-vfx-toggle">
                <input type="checkbox" checked={form.vfx}
                  onChange={e => setForm({...form, vfx: e.target.checked})} />
                <Sparkles size={12} /> VFX shot — needs plates or CG element
              </label>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={()=>setOpenAdd(false)} data-testid="shot-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-5"
                data-testid="shot-submit">
                {saving ? "Saving…" : "Add shot"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShotRow({ shot, onToggle, onRemove }) {
  const meta = STATUS_META[shot.status || "planned"];
  return (
    <div className="grid grid-cols-12 gap-3 items-center py-2.5 px-3 rounded-xl border border-white/5 hover:border-white/15"
      data-testid={`shot-row-${shot.id}`}>
      <button onClick={() => onToggle(shot)} className="col-span-1 flex items-center justify-center"
        title={`Status: ${meta.label} · click to advance`}
        data-testid={`shot-toggle-${shot.id}`}>
        <span className="w-6 h-6 rounded-full border-2 flex items-center justify-center"
          style={{ borderColor: meta.color, background: `${meta.color}22`, color: meta.color }}>
          {shot.status === "shot" ? <Check size={12} /> : shot.status === "skipped" ? "×" : ""}
        </span>
      </button>
      <div className="col-span-1 text-sm font-display text-white/85">{shot.shot_number}</div>
      <div className="col-span-5 min-w-0">
        <div className="text-sm truncate">{shot.description || <span className="text-white/40 italic">No description</span>}</div>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
          {shot.lens && <span className="text-[10px] uppercase tracking-[0.18em] text-white/50"><Camera size={9} className="inline mr-0.5" />{shot.lens}</span>}
          {shot.camera_movement && shot.camera_movement !== "static" && (
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/50">{shot.camera_movement}</span>
          )}
          {shot.vfx && <span className="text-[10px] uppercase tracking-[0.18em] text-fuchsia-300"><Sparkles size={9} className="inline mr-0.5" /> VFX</span>}
        </div>
      </div>
      <div className="col-span-2 text-xs text-white/60 truncate">
        {shot.cast_in_shot?.join(", ") || <span className="text-white/30">—</span>}
      </div>
      <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] text-white/55">
        {shot.shoot_day || <span className="text-white/30">Unassigned</span>}
      </div>
      <div className="col-span-1 text-right">
        <button onClick={() => onRemove(shot.id)} className="p-2 text-white/40 hover:text-red-400"
          data-testid={`shot-remove-${shot.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
